import React, { ReactElement } from 'react';
import { createIntl, createIntlCache, FormattedNumber } from 'react-intl/dist';
import { LocaleService } from 'internal-services/locale/locale.service';

const cache = createIntlCache();

const NS = 'IntlService';

const CURRENCY_FORMAT_OPTIONS = {
  style: 'currency' as const,
  currencyDisplay: 'symbol' as const,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * IntlService — React currency formatting helper.
 *
 * Locale is sourced from LocaleService.getLocale() which reads
 * window.config.supportedLanguages and matches against navigator.language.
 * All currency methods use Intl.NumberFormat (via react-intl) so symbol
 * placement and separators are locale-correct — no hardcoded '$' prefixes.
 */
export class IntlService {
  static intl = createIntl({ locale: LocaleService.getLocale() }, cache);

  // Kept for legacy callers; all currency now goes through Intl.NumberFormat
  static isDollarCurrency(currency: string): boolean {
    return currency === 'AUD' || currency === 'USD' || currency === 'NZD' || currency === 'CAD';
  }

  /** Returns a locale-formatted currency as a React element (uses FormattedNumber). Requires IntlProvider when rendered. */
  static currencyValue(val: number, currency: string): ReactElement {
    if (!val) {
      return null;
    }

    return (
      <FormattedNumber
        currencyDisplay="symbol"
        value={val}
        style="currency"
        minimumFractionDigits={2}
        maximumFractionDigits={2}
        currency={currency}
      />
    );
  }

  /** Returns a locale-formatted currency as a plain string. */
  static currencyValueAsString(val: string | number, currency: string): string {
    if (!val) {
      return '';
    }

    if (typeof val === 'string') {
      val = parseFloat(val);
    }

    return IntlService.intl.formatNumber(val, {
      ...CURRENCY_FORMAT_OPTIONS,
      currency,
    });
  }

  /** Extracts the currency symbol for the given ISO code (e.g. 'AUD' → '$'). */
  static getCurrencySymbol(currency: string): {symbol: string, goesBefore: boolean} {
    const currencyParts = IntlService.intl.formatNumberToParts(0, { style: 'currency', currency: currency });
    const symbol = currencyParts[0].value;

    return {symbol, goesBefore: true};
  }
}
